import { createContext, useContext, useEffect, useSyncExternalStore } from 'react';
import type { EventDefinition } from '../types';

type EventsState = {
  events: EventDefinition[];
  isLoading: boolean;
};

const defaultState: EventsState = {
  events: [],
  isLoading: true,
};

const EventsContext = createContext<EventsState>(defaultState);

// Cache for loaded events
let cachedEvents: EventDefinition[] | null = null;
let loadPromise: Promise<EventDefinition[]> | null = null;
const listeners = new Set<() => void>();

// Stable empty array reference to prevent creating new arrays
const EMPTY_EVENTS: EventDefinition[] = [];

// Cache the snapshot object to prevent creating new objects on every call
let cachedSnapshot: EventsState = {
  events: EMPTY_EVENTS,
  isLoading: true,
};

function getSnapshot(): EventsState {
  const newEvents = cachedEvents || EMPTY_EVENTS;
  const newIsLoading = cachedEvents === null;
  
  // Only update snapshot if values actually changed (reference equality)
  if (
    cachedSnapshot.events !== newEvents ||
    cachedSnapshot.isLoading !== newIsLoading
  ) {
    cachedSnapshot = {
      events: newEvents,
      isLoading: newIsLoading,
    };
  }
  
  return cachedSnapshot;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

async function loadEvents(): Promise<EventDefinition[]> {
  // If already cached, return immediately
  if (cachedEvents !== null) {
    return cachedEvents;
  }

  // If already loading, return the existing promise
  if (loadPromise !== null) {
    return loadPromise;
  }

  // Start loading
  loadPromise = import('../data/events')
    .then((module) => {
      cachedEvents = module.META_EVENTS;
      // Clear cached snapshot so getSnapshot creates a new one with new data
      cachedSnapshot = {
        events: cachedEvents,
        isLoading: false,
      };
      emit(); // Notify all listeners that data is ready
      return cachedEvents;
    })
    .catch((error) => {
      console.error('Failed to load events:', error);
      // Reset loadPromise so we can retry
      loadPromise = null;
      throw error;
    });

  return loadPromise;
}

export function EventsProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Start loading events immediately
    loadEvents().catch((error) => {
      console.error('Error loading events:', error);
    });
  }, []);

  const state = useSyncExternalStore(subscribe, getSnapshot);

  return (
    <EventsContext.Provider value={state}>
      {children}
    </EventsContext.Provider>
  );
}

export function useEvents() {
  return useContext(EventsContext);
}

